import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter_paging';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination_paging';
import CheckOutModal from 'components/modal/CheckOutModal';
import paths from 'routes/paths';
import { fetchAdminList, deleteMultiAdminInfo } from 'hooks/CallApi';
import { formatDate } from 'components/common/SunnyUtils';
import { useAppContext } from 'providers/AppProvider';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';

// --- Styled Components ---
const PageContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c3d4fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1.5rem;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.1);
`;

const PageTitle = styled.h2`
  margin: 0;
  font-weight: 700;
  color: #1e293b;
  font-size: 1.75rem;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 0.95rem;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }
`;

const columns = [
  {
    accessorKey: 'DuserRole',
    header: '역할',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'companyName',
    header: '소속',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'userId',
    header: '아이디',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'username',
    header: '이름',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'active',
    header: '상태',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'createdAt',
    header: '등록일시',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'updateAt',
    header: '최근접속일시',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  }
];

function AdvanceTableExample() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]); // 데이터 상태
  const [modalShow, setModalShow] = useState(false);
  const [modalReShow, setModalReShow] = useState(false);
  const [loading, setLoading] = useState(true); // 로딩 상태
  const [showSpinner, setShowSpinner] = useState(false); // 스피너 상태 추가
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 현재 페이지
  const [perPage, setPerPage] = useState(10); // 페이지당 항목 수 (size)
  const [ErrorCode, setErrorCode] = useState('');
  const [ErrorMsg, setErrorMsg] = useState('');
  const [modalErrorShow, setModalErrorShow] = useState(false);

  const { setConfig, config } = useAppContext();

  const table = useAdvanceTable({
    data: tableData,
    columns,
    sortable: true,
    selection: true,
    pagination: true,
    perPage,
    selectionColumnWidth: 30
  });

  // API 호출 함수
  const loadData = async (page, size) => {
    const spinnerTimer = setTimeout(() => setShowSpinner(true), 300);

    try {
      const response = await fetchAdminList(page, size); // API 호출
      const logData = response.data.content.map((item, index) => {
        // 현재 사용자가 자기 자신인지 확인
        const isSelf = item.userIds.userUuid === config.userUuid;

        // 현재 사용자가 ADMIN이고 해당 행이 SA인지 확인
        const isAdminTryingToSelectSA = config.userAuth === 'ADMIN' && item.userRole === 'SA';

        return {
          num: page * size + index + 1, // 역순으로 순번 계산
          userId: item.userId,
          username: item.username,
          userRole: item.userRole,
          DuserRole:
            item.userRole === 'SA'
              ? '시스템 관리자'
              : item.userRole === 'ADMIN'
                ? '최고 관리자'
                : '운영 관리자',
          userUuid: item.userIds.userUuid,
          companyId: item.userIds.companyId,
          active: item.active ? '정상' : '비정상',
          activestatus: item.active,
          description: item.userDescription,
          companyName: item.companyName,
          imgUrl: item.imgUrl,
          createdAt: item.insertTimeUtc
            ? formatDate(item.insertTimeUtc) // 포맷 함수 호출
            : '-',
          updateAt: item.loginTimeUtc
            ? formatDate(item.loginTimeUtc) // 포맷 함수 호출
            : '-',
          disabled: isSelf || isAdminTryingToSelectSA // 자기 자신이거나 ADMIN이 SA를 선택하려는 경우 비활성화
        };
      });
      setTotalElements(response.data.page.totalElements);
      setTableData(logData);
    } catch (error) {
      console.error('API 데이터 로드 에러:', error);
    } finally {
      clearTimeout(spinnerTimer); // 데이터 로딩이 끝나면 스피너 타이머 제거
      setLoading(false);
      setShowSpinner(false); // 로딩 완료 후 스피너를 숨김
    }
  };

  // 페이지 변경 시 데이터 다시 로드
  useEffect(() => {
    loadData(currentPage, perPage);
    setConfig('checkorg', '');
    setConfig('isNavbarVerticalCollapsed', true); // 선택된 고객사 이름 설정
  }, [currentPage, perPage]);

  // 페이지네이션 이벤트 핸들러
  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    table.resetRowSelection(); // 선택된 행 초기화
  };

  const handlePerPageChange = newPerPage => {
    setPerPage(newPerPage); // 페이지 크기 업데이트
    setCurrentPage(0); // 첫 페이지로 이동
  };

  const handleRowClick = row => {
    const {
      userId,
      username,
      userRole,
      userUuid,
      companyId,
      activestatus,
      companyName,
      createdAt,
      description,
      updateAt,
      imgUrl
    } = row.original;
    navigate(`/safedb/SA/adminAccount/edit`, {
      state: {
        createdAt,
        updateAt,
        activestatus,
        userUuid,
        userId,
        username,
        companyName,
        userRole,
        description,
        companyId,
        imgUrl
      } // 전달할 데이터
    });

  };

  const handleDeleteClick = () => {
    setModalShow(true);
  };

  const selectedRowCount = table.getSelectedRowModel().rows.length;

  const handleDelete = async () => {
    setModalShow(false);

    const selecteduserUuids = table
      .getSelectedRowModel()
      .rows.map(row => row.original.userUuid); // 선택된 행의 companyId 가져오기
    try {
      await deleteMultiAdminInfo(selecteduserUuids); // 삭제 API 호출

      setModalReShow(true);
      table.resetRowSelection();
    } catch (error) {
      setErrorCode(error.response.data.code);
      setErrorMsg(error.response.data.message);
      setModalErrorShow(true);
    }
  };

  const handleDeleteRe = () => {
    setModalReShow(false);
    window.location.reload(); // 등록 완료 후 페이지 새로고침
  };

  return (
    <AdvanceTableProvider {...table}>
      <StyledCard>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
        {/* <AdvanceTableFooter rowsPerPageSelection /> */}
        <AdvanceTableFooter
          rowsPerPageSelection
          onPageChange={(page, newPageSize) => {
            handlePerPageChange(newPageSize); // 페이지 크기 변경
            setCurrentPage(page); // 현재 페이지 설정
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {selectedRowCount > 0 ? (
            <Button
              variant="danger"
              style={{ width: '100px', borderRadius: '8px', fontWeight: 700, boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)' }}
              onClick={handleDeleteClick}
            >
              삭제
            </Button>
          ) : (
            <Link to={paths.addadminAccount} style={{ textDecoration: 'none' }}>
              <Button variant="success" style={{ width: '100px', borderRadius: '8px', fontWeight: 700, boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)' }}>
                등록
              </Button>
            </Link>
          )}
        </div>
      </div>
      {loading ? (
        showSpinner ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <p></p>
        )
      ) : (
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-800"
          rowClassName="align-middle white-space-nowrap border-bottom border-200"
          tableProps={{
            hover: true,
            className: 'fs-10 mb-0 overflow-hidden text-700'
          }}
          onRowClick={handleRowClick}
        />
      )}
        <Card.Footer className="bg-light py-3 border-top">
          <div className="fs--1 fw-semi-bold text-500 mb-2">
            총 <span className="text-primary fw-bold">{totalElements.toLocaleString()}</span>명
          </div>
          <div className="d-flex justify-content-center">
            <AdvanceTablePagination
              currentPage={currentPage}
              totalItems={totalElements}
              itemsPerPage={perPage}
              onChange={newPage => handlePageChange(newPage)}
            />
          </div>
        </Card.Footer>
      </StyledCard>

      <CheckOutModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onConfirm={handleDelete}
        title="경고"
        body="삭제된 관리자 정보는 복구할 수 없습니다. 정말 삭제하시겠습니까?"
        cancelText="취소"
        confirmText="삭제"
        variant="warning"
        CancelButton={true}
        SubmitButton={true}
      />
      <CheckOutModal
        show={modalReShow}
        onHide={() => setModalReShow(false)}
        onConfirm={handleDeleteRe}
        title="완료"
        body="삭제가 완료 되었습니다."
        confirmText="확인"
        variant="success"
        SubmitButton={true}
      />
      <CheckOutModal
        show={modalErrorShow}
        onHide={() => setModalErrorShow(false)}
        onConfirm={() => setModalErrorShow(false)}
        title="경고"
        // body={`에러코드 : ${ErrorCode}, 에러 메시지 : ${ErrorMsg}`}
        body={`${ErrorMsg}`}
        cancelText="취소"
        confirmText="확인"
        variant="warning"
        SubmitButton={true}
      />
    </AdvanceTableProvider>
  );
}

const AdminAccount = () => {
  return (
    <PageContainer>
      <HeaderSection>
        <TitleWrapper>
          <IconWrapper>
            <FontAwesomeIcon icon={faUsers} />
          </IconWrapper>
          <div>
            <PageTitle>관리자 계정</PageTitle>
            <PageSubtitle>플랫폼 및 시스템 관리자 계정을 등록 및 관리합니다.</PageSubtitle>
          </div>
        </TitleWrapper>
      </HeaderSection>
      <AdvanceTableExample />
    </PageContainer>
  );
};

export default AdminAccount;
